import React, { useState, useEffect } from 'react';
import { differenceInDays, isDate } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import MLIcon from '../../../../../../components/MLIcon/MLIcon';
import { DatePicker } from './components/DatePicker/DatePicker';
import s from './CoursePerformanceSelect.module.css';
import { formatDate, formatDateRange } from './utils';
import { getInsightsTag } from '../../helpers';
import { INSIGHTS_MENU } from '../../../../constants';

const RANGE_LABELS = {
  LAST_7: 'Last 7 days',
  LAST_30: 'Last 30 days',
  ALL_TIME: 'All Time',
  CUSTOM: 'Custom Range',
};

const getLabelFromDateRange = ({ minRangeDays, maxRangeDays }, isCustom) => {
  if (isCustom) {
    return formatDateRange({ minRangeDays, maxRangeDays });
  }

  if (maxRangeDays === 0) {
    if (minRangeDays === 7) return RANGE_LABELS.LAST_7;
    if (minRangeDays === 30) return RANGE_LABELS.LAST_30;
  } else if (maxRangeDays === null && minRangeDays === null) {
    return RANGE_LABELS.ALL_TIME;
  }

  return formatDateRange({ minRangeDays, maxRangeDays });
};

export const CoursePerformanceSelect = ({
  onDateChange,
  dateRange,
  isCustomRange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDatepicker, setIsDatepicker] = useState(false);
  const [label, setLabel] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    if (!isEmpty(dateRange)) {
      setLabel(getLabelFromDateRange(dateRange, isCustomRange));
    }
  }, [dateRange, isCustom, isCustomRange]);

  const toggleExpanded = () => {
    setIsDatepicker(false);
    setIsExpanded((prev) => !prev);
  };

  const onCustomDateSelect = ({ start, end }) => {
    const maxRangeDays = differenceInDays(end, new Date());
    const minRangeDays = Math.abs(differenceInDays(start, new Date()));
    setIsCustom(true);
    setLabel(`${formatDate(start)} - ${formatDate(end)}`);
    onDateChange({ maxRangeDays, minRangeDays }, isCustom);
  };

  const createDataId = () =>
    `${getInsightsTag(INSIGHTS_MENU.COURSE_PERFORMANCE)}-dropdown-${label
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/last|range/g, '')
      .trim()}`;

  const onSelect = (range) => {
    if (range === RANGE_LABELS.CUSTOM) {
      setIsDatepicker(true);
      setIsCustom(true);
    } else {
      setIsCustom(false);
      setIsExpanded(false);
      setLabel(range);
      setCustomLabel('');

      const rangeMap = {
        [RANGE_LABELS.LAST_7]: { maxRangeDays: 0, minRangeDays: 7 },
        [RANGE_LABELS.LAST_30]: { maxRangeDays: 0, minRangeDays: 30 },
        [RANGE_LABELS.ALL_TIME]: { maxRangeDays: null, minRangeDays: null },
      };

      if (rangeMap[range]) {
        onDateChange(rangeMap[range]);
      }
    }
  };

  const onDateRangeClick = (range) => {
    const { start, end } = range;
    if (isDate(start) && !end) {
      setCustomLabel(`${formatDate(start)} - `);
    } else if (start && end) {
      setCustomLabel(`${formatDate(start)} - ${formatDate(end)}`);
    }
  };

  return (
    <div
      className={s.dateSelectDropdown}
      onMouseLeave={() => {
        setCustomLabel('');
        setIsExpanded(false);
      }}
    >
      <div className={s.dropdownWrapper}>
        <label>Date Range: </label>
        <button
          className={s.selectButton}
          onClick={toggleExpanded}
          data-test-id={createDataId()}
        >
          <MLIcon className={s.calendarIcon} type="calendar" />
          <span>{customLabel || label}</span>
          <MLIcon className={s.caretIcon} type="caret_down" />
        </button>
      </div>

      {isExpanded && (
        <div className={s.dropdown}>
          {isDatepicker ? (
            <DatePicker
              onSelect={onDateRangeClick}
              onCancel={() => {
                setCustomLabel('');
                setIsExpanded(false);
              }}
              onSave={(range) => {
                onCustomDateSelect(range);
                setIsExpanded(false);
              }}
            />
          ) : (
            <div className={s.dropdownItems}>
              {Object.values(RANGE_LABELS).map((val) => (
                <div
                  key={val}
                  role="button"
                  className={
                    (isCustomRange && val === RANGE_LABELS.CUSTOM) ||
                    (label === val && !isCustomRange)
                      ? s.selected
                      : ''
                  }
                  onPointerUp={() => onSelect(val)}
                >
                  {val}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
